import type {
  CreateFilthBackendInput,
  FilthBackend,
  UpdateFilthBackendInput,
} from "@/api/filth-backends-api"

export type FilthBackendFormState = {
  id: string
  name: string
  base_url: string
  tenant_id: string
  default_scope: string
  consumer_mode: "off" | "shadow" | "primary"
  delegated_scope: string
  company_wide: boolean
  token: string
  /** Name of a credential the operator pre-provisioned as HAI_SECRET_<NAME> (file config backend). */
  credential_secret_name: string
}

export const EMPTY_FILTH_BACKEND_FORM: FilthBackendFormState = {
  id: "",
  name: "",
  base_url: "",
  tenant_id: "",
  default_scope: "",
  consumer_mode: "off",
  delegated_scope: "",
  company_wide: true,
  token: "",
  credential_secret_name: "",
}

export function formStateFromBackend(backend: FilthBackend): FilthBackendFormState {
  return {
    id: backend.id,
    name: backend.name,
    base_url: backend.base_url,
    tenant_id: backend.tenant_id ?? "",
    default_scope: backend.default_scope ?? "",
    consumer_mode: backend.consumer_mode ?? "off",
    delegated_scope: backend.delegated_scope ?? "",
    company_wide: backend.company_wide ?? true,
    // Both credential fields start blank: blank means "keep the current credential".
    token: "",
    credential_secret_name: "",
  }
}

/** The backend refuses a payload carrying both a token and a pre-provisioned name. */
export function hasCredentialConflict(form: FilthBackendFormState): boolean {
  return form.token !== "" && form.credential_secret_name.trim() !== ""
}

/**
 * Exactly one credential source, or none: an inline token (stored in Key Vault)
 * or the name of a pre-provisioned secret (file config backend, no Key Vault).
 */
function credentialFields(
  form: FilthBackendFormState
): Pick<CreateFilthBackendInput, "token" | "credential_secret_name"> {
  const secretName = form.credential_secret_name.trim()
  if (secretName) return { credential_secret_name: secretName }
  if (form.token) return { token: form.token }
  return {}
}

function sharedFields(form: FilthBackendFormState) {
  return {
    name: form.name,
    base_url: form.base_url,
    tenant_id: form.tenant_id || undefined,
    default_scope: form.consumer_mode === "primary" ? "" : form.default_scope || undefined,
    consumer_mode: form.consumer_mode,
    delegated_scope: form.delegated_scope || undefined,
    company_wide: form.company_wide,
  }
}

export function buildCreateFilthBackendInput(form: FilthBackendFormState): CreateFilthBackendInput {
  return {
    id: form.id.trim().toLowerCase(),
    ...sharedFields(form),
    ...credentialFields(form),
  }
}

export function buildUpdateFilthBackendInput(form: FilthBackendFormState): UpdateFilthBackendInput {
  return {
    ...sharedFields(form),
    ...credentialFields(form),
  }
}
